import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, ArrowLeft, Target, CheckCircle2, FileText, TrendingUp,
  Award, Brain, Lightbulb, Star, Clock, BookOpen, BarChart3,
  Sparkles, ChevronRight, Medal, Zap, Trophy, GraduationCap, LogOut, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * StudentDashboard - Central dashboard for tracking learner progress across all labs
 * Displays AI Capability Score, Workforce Readiness, lab completion, portfolio outputs
 * 
 * Connected to authenticated user data from backend
 */
export default function StudentDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/api/progress/dashboard`, {
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch dashboard data');
        }
        
        const data = await response.json();
        setDashboardData(data);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/juswork/prolab');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A] mx-auto mb-4" />
          <p className="text-[#1E3A8A]">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Use fetched data or defaults
  const data = dashboardData || getDefaultDashboardData();
  
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="student-dashboard">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A8A] to-[#2563EB] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/juswork/prolab" 
              className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to ProLab
            </Link>
            
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white text-sm font-medium mb-4">
                  <GraduationCap className="w-4 h-4" />
                  Learner Intelligence
                </div>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-2">
                  Student Intelligence Dashboard
                </h1>
                <p className="text-lg text-white/80">
                  Track your AI workforce capability development journey
                </p>
              </div>
              
              {/* Key Scores */}
              <div className="flex flex-wrap gap-4">
                <ScoreCard 
                  label="AI Capability Score" 
                  value={data.scores?.ai_capability_score || 0} 
                  icon={Brain}
                  color="#F97316"
                />
                <ScoreCard 
                  label="Workforce Readiness" 
                  value={data.scores?.workforce_readiness_score || 0} 
                  icon={TrendingUp}
                  color="#10B981"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-[#E5E7EB]">
            <StatItem icon={BookOpen} label="Labs Enrolled" value={data.stats?.labs_enrolled || 0} />
            <StatItem icon={CheckCircle2} label="Labs Completed" value={data.stats?.labs_completed || 0} />
            <StatItem icon={Target} label="Missions Done" value={data.stats?.missions_completed || 0} />
            <StatItem icon={FileText} label="Outputs Generated" value={data.stats?.outputs_generated || 0} />
            <StatItem icon={Clock} label="Hours Learning" value={`${data.stats?.learning_hours || 0}h`} />
          </div>
        </div>
      </section>

      {/* User Info & Logout */}
      <section className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#1E3A8A]">
                Welcome, <span className="font-semibold">{user?.name || data.user?.name || 'Learner'}</span>
              </span>
              {data.scores?.scores_provisional && (
                <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                  Scores Provisional
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#1E3A8A]/70 hover:text-[#991B1B]">
              <LogOut className="w-4 h-4 mr-1" />
              Sign Out
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-[#E5E7EB] p-1 h-auto flex-wrap">
            <TabsTrigger value="overview" className="gap-2 data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4" /> Overview
            </TabsTrigger>
            <TabsTrigger value="labs" className="gap-2 data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white">
              <BookOpen className="w-4 h-4" /> My Labs
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="gap-2 data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white">
              <FileText className="w-4 h-4" /> Portfolio
            </TabsTrigger>
            <TabsTrigger value="achievements" className="gap-2 data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white">
              <Trophy className="w-4 h-4" /> Achievements
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Main Column */}
              <div className="lg:col-span-2 space-y-6">
                {/* Capability Breakdown */}
                <Card className="border-[#E5E7EB] bg-white">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl" style={{ color: '#991B1B' }}>
                      Capability Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(data.capabilities || []).map((cap, index) => (
                        <CapabilityBar key={index} capability={cap} />
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card className="border-[#E5E7EB] bg-white">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl" style={{ color: '#991B1B' }}>
                      Recent Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(data.recentActivity || []).map((activity, index) => (
                        <ActivityItem key={index} activity={activity} />
                      ))}
                      {(!data.recentActivity || data.recentActivity.length === 0) && (
                        <p className="text-sm text-[#1E3A8A]/50 text-center py-4">
                          Complete missions to see your activity here
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Recommended Next Labs */}
                <Card className="border-[#E5E7EB] bg-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base" style={{ color: '#991B1B' }}>
                      <Lightbulb className="w-4 h-4 inline mr-2" />
                      Recommended Next
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(data.recommended_labs || data.recommendedLabs || []).map((labId, index) => (
                      <Link key={index} to={`/juswork/prolab/${typeof labId === 'string' ? labId : labId.id}`}>
                        <div className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E7EB] hover:shadow-md transition-shadow group">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: '#1E3A8A15' }}
                          >
                            <Sparkles className="w-5 h-5" style={{ color: '#1E3A8A' }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#1E3A8A] truncate">
                              {typeof labId === 'string' ? labId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : labId.name}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#1E3A8A]/40 group-hover:text-[#1E3A8A] transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </CardContent>
                </Card>

                {/* Skill Badges */}
                <Card className="border-[#E5E7EB] bg-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base" style={{ color: '#991B1B' }}>
                      <Medal className="w-4 h-4 inline mr-2" />
                      Skill Badges Earned
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {(data.badges_earned || data.badges || []).map((badge, index) => (
                        <Badge 
                          key={index}
                          variant="outline"
                          className="py-1.5 px-3"
                          style={{ 
                            backgroundColor: '#10B98115', 
                            color: '#10B981',
                            borderColor: '#10B98130'
                          }}
                        >
                          {typeof badge === 'string' ? badge.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : badge.name}
                        </Badge>
                      ))}
                      {(!data.badges_earned || data.badges_earned.length === 0) && (!data.badges || data.badges.length === 0) && (
                        <p className="text-sm text-[#1E3A8A]/50">Complete missions to earn badges</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Stats */}
                <Card className="border-[#E5E7EB] bg-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base" style={{ color: '#991B1B' }}>
                      <Zap className="w-4 h-4 inline mr-2" />
                      This Week
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]">
                        <p className="text-2xl font-bold text-[#1E3A8A]">{data.weeklyMissions}</p>
                        <p className="text-xs text-[#1E3A8A]/60">Missions</p>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]">
                        <p className="text-2xl font-bold text-[#1E3A8A]">{data.weeklyOutputs}</p>
                        <p className="text-xs text-[#1E3A8A]/60">Outputs</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* My Labs Tab */}
          <TabsContent value="labs">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(data.enrolled_labs || data.enrolledLabs || []).map((lab, index) => (
                <LabProgressCard key={index} lab={lab} />
              ))}
            </div>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <Card className="border-[#E5E7EB] bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-xl" style={{ color: '#991B1B' }}>
                  Portfolio Artifacts
                </CardTitle>
                <p className="text-sm text-[#1E3A8A]/70">
                  Outputs generated across all labs
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(data.portfolio_outputs || data.portfolioOutputs || []).map((output, index) => (
                    <PortfolioCard key={index} output={output} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Achievements Tab */}
          <TabsContent value="achievements">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {getDefaultAchievements().map((achievement, index) => (
                <AchievementCard key={index} achievement={{
                  ...achievement,
                  earned: (data.badges_earned || []).includes(achievement.id)
                }} />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

function ScoreCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 min-w-[140px]">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5" style={{ color }} />
        <span className="text-xs text-white/70">{label}</span>
      </div>
      <p className="text-3xl font-bold font-mono" style={{ color }}>{value}%</p>
    </div>
  );
}

function StatItem({ icon: Icon, label, value }) {
  return (
    <div className="py-4 px-6 text-center">
      <Icon className="w-5 h-5 mx-auto mb-1 text-[#1E3A8A]" />
      <p className="text-xl font-bold text-[#1E3A8A]">{value}</p>
      <p className="text-xs text-[#1E3A8A]/60">{label}</p>
    </div>
  );
}

function CapabilityBar({ capability }) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="font-medium text-[#1E3A8A]">{capability.name}</span>
        <span className="font-mono text-[#1E3A8A]/70">{capability.score}%</span>
      </div>
      <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
        <motion.div 
          className="h-full rounded-full"
          style={{ backgroundColor: capability.color }}
          initial={{ width: 0 }}
          animate={{ width: `${capability.score}%` }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />
      </div>
    </div>
  );
}

function ActivityItem({ activity }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]">
      <div 
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${activity.color}15` }}
      >
        {activity.type === 'mission' && <Target className="w-4 h-4" style={{ color: activity.color }} />}
        {activity.type === 'output' && <FileText className="w-4 h-4" style={{ color: activity.color }} />}
        {activity.type === 'badge' && <Award className="w-4 h-4" style={{ color: activity.color }} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#1E3A8A]">{activity.title}</p>
        <p className="text-xs text-[#1E3A8A]/50">{activity.lab} • {activity.time}</p>
      </div>
    </div>
  );
}

function LabProgressCard({ lab }) {
  return (
    <Link to={`/juswork/prolab/${lab.id}`}>
      <Card className="border-[#E5E7EB] bg-white hover:shadow-lg transition-all h-full">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${lab.color}15` }}
            >
              <BookOpen className="w-6 h-6" style={{ color: lab.color }} />
            </div>
            <Badge 
              variant="outline"
              className={lab.progress >= 100 
                ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' 
                : 'bg-[#1E3A8A]/10 text-[#1E3A8A] border-[#1E3A8A]/20'
              }
            >
              {lab.progress >= 100 ? 'Complete' : 'In Progress'}
            </Badge>
          </div>
          <h4 className="font-heading font-semibold text-[#111827] mb-1">{lab.name}</h4>
          <p className="text-xs text-[#1E3A8A]/60 mb-4">{lab.category}</p>
          
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-[#1E3A8A]/70">Progress</span>
                <span className="font-mono text-[#1E3A8A]">{lab.progress}%</span>
              </div>
              <Progress value={lab.progress} className="h-1.5" />
            </div>
            <div className="flex justify-between text-xs text-[#1E3A8A]/60">
              <span>{lab.missionsCompleted}/{lab.totalMissions} missions</span>
              <span>{lab.outputs} outputs</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function PortfolioCard({ output }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-lg border border-[#E5E7EB] bg-white hover:shadow-md transition-shadow">
      <div 
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${output.color}15` }}
      >
        <FileText className="w-5 h-5" style={{ color: output.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#111827] truncate">{output.title}</p>
        <p className="text-xs text-[#1E3A8A]/50">{output.type}</p>
        <p className="text-xs text-[#1E3A8A]/40 mt-1">{output.lab}</p>
      </div>
    </div>
  );
}

function AchievementCard({ achievement }) {
  return (
    <Card className={`border-[#E5E7EB] ${achievement.earned ? 'bg-white' : 'bg-[#F8FAFC] opacity-60'}`}>
      <CardContent className="p-5 text-center">
        <div 
          className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
          style={{ 
            backgroundColor: achievement.earned ? `${achievement.color}15` : '#E5E7EB',
          }}
        >
          <Trophy 
            className="w-8 h-8" 
            style={{ color: achievement.earned ? achievement.color : '#9CA3AF' }} 
          />
        </div>
        <h4 className="font-heading font-semibold text-[#111827] mb-1">{achievement.name}</h4>
        <p className="text-xs text-[#1E3A8A]/60 mb-3">{achievement.description}</p>
        {achievement.earned ? (
          <Badge className="bg-[#10B981] text-white">Earned</Badge>
        ) : (
          <Badge variant="outline" className="text-[#9CA3AF] border-[#E5E7EB]">Locked</Badge>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// DEFAULT DATA (for initial render before API data loads)
// ============================================

function getDefaultDashboardData() {
  return {
    user: { name: 'Learner', email: '' },
    scores: {
      ai_capability_score: 0,
      workforce_readiness_score: 0,
      readiness_level: 'Beginner',
      scores_provisional: true
    },
    stats: {
      labs_enrolled: 0,
      labs_completed: 0,
      missions_completed: 0,
      outputs_generated: 0,
      learning_hours: 0
    },
    capabilities: [
      { name: "Business & Entrepreneurship", score: 0, color: "#1E3A8A" },
      { name: "Leadership & Communication", score: 0, color: "#2563EB" },
      { name: "Quality & Safety", score: 0, color: "#3B82F6" },
      { name: "Industry Operations", score: 0, color: "#60A5FA" },
      { name: "Security Services", score: 0, color: "#991B1B" }
    ],
    enrolled_labs: [],
    portfolio_outputs: [],
    badges_earned: [],
    recommended_labs: ["ai-entrepreneurship", "leadership", "tqm"]
  };
}

function getDefaultAchievements() {
  return [
    { id: "first_mission", name: "First Steps", description: "Complete your first mission", color: "#10B981" },
    { id: "portfolio_starter", name: "Portfolio Builder", description: "Generate 5 portfolio outputs", color: "#3B82F6" },
    { id: "lab_starter", name: "Lab Explorer", description: "Enroll in 3 different labs", color: "#2563EB" },
    { id: "mission_master", name: "Mission Master", description: "Complete 10 missions", color: "#1E3A8A" },
    { id: "lab_champion", name: "Lab Champion", description: "Complete an entire lab", color: "#F97316" },
    { id: "multi_skilled", name: "Multi-Skilled", description: "Progress in 3 different categories", color: "#991B1B" }
  ];
}
